import { useState } from "react";
import api, { apiError, downloadFile, localApi } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  UsersThree, Money, Fingerprint, CalendarDots, CalendarPlus, ClipboardText,
  FileXls, Download, Upload, CircleNotch, HandCoins, Medal,
} from "@phosphor-icons/react";

const REPORTS = [
  { key: "employees", title: "Empleados", desc: "Listado completo con datos laborales y bancarios", icon: UsersThree, path: "/report-tools/employees.xlsx", file: "empleados.xlsx" },
  { key: "payrolls", title: "Planillas", desc: "Historial de planillas con deducciones y netos", icon: Money, path: "/report-tools/payrolls.xlsx", file: "planillas.xlsx" },
  { key: "attendance", title: "Asistencia", desc: "Registro de marcaciones de entrada y salida", icon: Fingerprint, path: "/report-tools/attendance.xlsx", file: "asistencia.xlsx" },
  { key: "schedules", title: "Horarios", desc: "Asignaciones de turnos programadas", icon: CalendarDots, path: "/report-tools/schedules.xlsx", file: "horarios.xlsx" },
  { key: "vacations", title: "Vacaciones", desc: "Saldos de vacaciones por empleado", icon: CalendarPlus, path: "/report-tools/vacations.xlsx", file: "vacaciones.xlsx" },
  { key: "leave-requests", title: "Solicitudes", desc: "Solicitudes de permisos e incapacidades", icon: ClipboardText, path: "/report-tools/leave-requests.xlsx", file: "solicitudes.xlsx" },
];

const TEMPLATES = [
  { key: "employees", title: "Empleados", path: "/reports/template-employees.xlsx", file: "plantilla-empleados.xlsx" },
  { key: "shifts", title: "Turnos", path: "/reports/template-shifts.xlsx", file: "plantilla-turnos.xlsx" },
  { key: "payrolls", title: "Planillas", path: "/reports/template-payrolls.xlsx", file: "plantilla-planillas.xlsx" },
  { key: "schedules", title: "Horarios", path: "/reports/template-schedules.xlsx", file: "plantilla-horarios.xlsx" },
];

export default function Reportes() {
  const [downloading, setDownloading] = useState("");
  const [importing, setImporting] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });

  const downloadExtra = async (kind) => {
    setDownloading("extra-" + kind);
    try {
      const params = {};
      if (range.from) params.from_date = range.from;
      if (range.to) params.to_date = range.to;
      const res = await localApi.get(`/report-extras/${kind}.xlsx`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind === "loans" ? "prestamos" : "comisiones"}_${range.from || "inicio"}_${range.to || "hoy"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte descargado");
    } catch (e) {
      toast.error(apiError(e, "No se pudo descargar el reporte."));
    } finally {
      setDownloading("");
    }
  };

  const download = async (r) => {
    setDownloading(r.key + r.path);
    try {
      if (r.path.startsWith("/report-tools/")) {
        const res = await localApi.get(r.path, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = r.file;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        await downloadFile(r.path, r.file);
      }
      toast.success(`${r.title} descargado`);
    } catch (e) {
      toast.error(apiError(e, "No se pudo descargar el reporte."));
    } finally {
      setDownloading("");
    }
  };

  const importEmployees = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reports/import-employees", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importación completada: ${data.created} creados, ${data.skipped} omitidos`);
      if (data.errors?.length) toast.warning(`${data.errors.length} filas con errores`);
    } catch (e) {
      toast.error(apiError(e, "No se pudo importar el archivo."));
    } finally {
      setImporting(false);
      ev.target.value = "";
    }
  };

  return (
    <div data-testid="reportes-page">
      <PageHeader title="Reportes" description="Exporta información en Excel y carga empleados masivamente" testid="reportes-header" />

      <h3 className="font-heading text-lg font-bold text-foreground mb-3 flex items-center gap-2">
        <FileXls size={18} className="text-muted-foreground" /> Exportaciones
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="reports-grid">
        {REPORTS.map((r) => (
          <div key={r.key} className="border border-border bg-card rounded-sm p-5 hover:border-foreground/40 hover:-translate-y-0.5 transition-[border-color,transform] duration-200" data-testid={`report-card-${r.key}`}>
            <r.icon size={22} className="text-muted-foreground" />
            <p className="font-heading text-base font-bold text-foreground mt-3">{r.title}</p>
            <p className="text-xs text-muted-foreground mt-1 min-h-[32px]">{r.desc}</p>
            <Button variant="outline" size="sm" disabled={downloading === r.key + r.path}
              className="mt-4 rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground"
              data-testid={`download-${r.key}`} onClick={() => download(r)}>
              {downloading === r.key + r.path ? <CircleNotch className="mr-2 animate-spin" size={14} /> : <Download className="mr-2" size={14} />}
              Descargar .xlsx
            </Button>
          </div>
        ))}
      </div>

      <h3 className="font-heading text-lg font-bold text-foreground mt-10 mb-3 flex items-center gap-2">
        <HandCoins size={18} className="text-muted-foreground" /> Control de préstamos y comisiones
      </h3>
      <div className="border border-border bg-card rounded-sm p-5" data-testid="extras-reports-card">
        <p className="text-xs text-muted-foreground">
          Reporte por período con lo pagado y lo pendiente: préstamos (saldos y abonos) y comisiones (pagadas en planilla vs. pendientes). Deja las fechas vacías para todo el historial.
        </p>
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <div className="space-y-1.5">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Desde</span>
            <Input type="date" className="rounded-sm border-input bg-background text-foreground w-40" data-testid="extras-report-from"
              value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Hasta</span>
            <Input type="date" className="rounded-sm border-input bg-background text-foreground w-40" data-testid="extras-report-to"
              value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </div>
          <Button variant="outline" size="sm" disabled={downloading === "extra-loans"}
            className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground"
            data-testid="download-loans-report" onClick={() => downloadExtra("loans")}>
            {downloading === "extra-loans" ? <CircleNotch className="mr-2 animate-spin" size={14} /> : <HandCoins className="mr-2" size={14} />}
            Préstamos .xlsx
          </Button>
          <Button variant="outline" size="sm" disabled={downloading === "extra-commissions"}
            className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground"
            data-testid="download-commissions-report" onClick={() => downloadExtra("commissions")}>
            {downloading === "extra-commissions" ? <CircleNotch className="mr-2 animate-spin" size={14} /> : <Medal className="mr-2" size={14} />}
            Comisiones .xlsx
          </Button>
        </div>
      </div>

      <h3 className="font-heading text-lg font-bold text-foreground mt-10 mb-3 flex items-center gap-2">
        <Upload size={18} className="text-muted-foreground" /> Importación masiva
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border bg-card rounded-sm p-5" data-testid="import-card">
          <p className="font-heading text-base font-bold text-foreground">Importar empleados</p>
          <p className="text-xs text-muted-foreground mt-1">Descarga la plantilla, complétala y súbela para crear empleados en masa.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="outline" size="sm" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground"
              data-testid="download-import-template" onClick={() => downloadFile("/reports/template-employees.xlsx", "plantilla-empleados.xlsx").catch((e) => toast.error(apiError(e)))}>
              <Download className="mr-2" size={14} /> Plantilla
            </Button>
            <label>
              <input type="file" accept=".xlsx" className="hidden" data-testid="import-employees-input" onChange={importEmployees} />
              <Button asChild disabled={importing} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80 cursor-pointer" size="sm">
                <span data-testid="import-employees-button">
                  {importing ? <CircleNotch className="mr-2 animate-spin" size={14} /> : <Upload className="mr-2" size={14} />}
                  Subir archivo
                </span>
              </Button>
            </label>
          </div>
        </div>

        <div className="border border-border bg-card rounded-sm p-5" data-testid="templates-card">
          <p className="font-heading text-base font-bold text-foreground">Plantillas de carga</p>
          <p className="text-xs text-muted-foreground mt-1">Formatos Excel oficiales para captura masiva.</p>
          <div className="mt-4 divide-y divide-border border border-border rounded-sm">
            {TEMPLATES.map((t) => (
              <div key={t.key} className="flex items-center justify-between p-3">
                <span className="text-sm text-foreground/80">{t.title}</span>
                <Button variant="ghost" size="sm" disabled={downloading === t.key + t.path}
                  className="text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`template-${t.key}`} onClick={() => download(t)}>
                  <Download className="mr-1.5" size={14} /> .xlsx
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
